import { useMemo } from 'react';
import { useList } from '@refinedev/core';
import { LiveFeedScreen } from '@viq-admin/screens';
import type { LiveFeedResponse } from '@viq-admin/screens';
import { superadminFetch } from '../../api/dataProvider';
import { getScopeCompanyId } from '../../scope/companyScope';
import { useCompanyScope } from '../../components/CompanyScopePicker';
import { LeadDetailBody } from '../Leads/LeadDetailBody';

interface ConfRow {
  id: string;
  name: string;
  location?: string | null;
  endDate?: string | null;
}

// Scope-aware fetcher factory. The tenant scope (or null for All tenants) is
// baked into the closure so the fetcher REFERENCE changes when the scope
// changes — useLiveFeed keys its poll effect on the fetcher identity, so a new
// ref resets the feed and re-polls under the new scope (the feed lives in hook
// state, not a query, so invalidateQueries() alone wouldn't reset it). The
// scope is read at call time; the arg only gates the closure identity.
const makeFetchLiveFeed =
  (_scopeCompanyId: string | null) =>
  async ({
    conferenceId,
    since,
    limit,
    signal,
  }: {
    conferenceId: string;
    since: string | null;
    limit: number;
    signal: AbortSignal;
  }): Promise<LiveFeedResponse> => {
    const params = new URLSearchParams();
    if (since) params.set('since', since);
    params.set('limit', String(limit));
    const scope = getScopeCompanyId();
    if (scope) params.set('companyId', scope);
    return superadminFetch<LiveFeedResponse>(
      `/v1/superadmin/conferences/${conferenceId}/live-feed?${params.toString()}`,
      { signal },
    );
  };

/**
 * Console Live-feed route. The default `/v1/superadmin/conferences` returns
 * ACTIVE conferences across ALL tenants (archived need `?archived=true`), so the
 * super-admin picker is inherently cross-tenant. Renders the shared LiveFeedScreen
 * with the console's fetcher + LeadDetailBody drawer.
 */
export function LiveFeedRoute() {
  const { companyId } = useCompanyScope();
  // Rebuild the fetcher when the scope changes so useLiveFeed resets + re-polls
  // under the new tenant. Conferences refetch via global invalidateQueries().
  const fetchLiveFeed = useMemo(() => makeFetchLiveFeed(companyId), [companyId]);

  const { result } = useList<ConfRow>({ resource: 'conferences' });
  const conferences = useMemo(
    () =>
      (result?.data ?? []).map((c) => ({
        id: c.id,
        name: c.name,
        location: c.location ?? null,
        endDate: c.endDate ?? null,
      })),
    [result?.data],
  );
  return (
    <LiveFeedScreen
      conferences={conferences}
      fetchLiveFeed={fetchLiveFeed}
      renderLeadDetail={(id) => <LeadDetailBody id={id} />}
    />
  );
}
